



































































































































































import {Component, Prop, Watch} from 'vue-property-decorator';
import CommonVue from '../models/commonVue';
import Skill from '../api/skill';
import School from '../api/school';
import ApiList from '../api/list';
import Media from '../api/media';
import LockManager from '../models/lock';

import {MediaFileField} from '../models/fields';
import {BusEvent} from '../models/bus';
import FieldImage from '../components/FieldImage.vue';
import FieldInternational from '../components/FieldInternational.vue';
import DropDownEntity from '../components/DropDownEntity.vue';
import DropDownText from '../components/DropDownText.vue';
import EditIcon from '../components/icons/EditIcon.vue';
import FieldVideos from "@/components/FieldVideos.vue";
import FieldInterfaceElements from "@/components/FieldInterfaceElements.vue";

@Component({
    components: {
        'field-interface-elements': FieldInterfaceElements,
        'drop-down-entity': DropDownEntity,
        'drop-down-text': DropDownText,
        'field-international': FieldInternational,
        'field-image': FieldImage,
        'edit-icon' : EditIcon,
        'field-videos': FieldVideos,
    },
})
export default class EditSkill extends CommonVue {
    @Prop({type: Number}) public id?: number;
    
    public schoolList: ApiList<School> = null;
    public skill: Skill = null;
    public skillName: string = '';
    
    // do not show schools if there is only 1 school
    public showSchools: boolean = false;
    
    public async beforeMount() {
        await this.load();
    }
    
    @Watch('id')
    public async load() {
        this.busy = true;
        this.error = false;

        this.ignoreFirstEntityChange = true;
        await this.loadSkill();
        this.ignoreFirstEntityChange = false;
        this.updateName();
        
        if (this.skill.id) {
            this.lockManager = new LockManager(Skill.API_SLUG, this.skill.id);
            await this.lockManager.checkStatus();
            if (this.lockManager.isBusy) {
                this.$bus.$emit(BusEvent.ERROR, 'Someone else is making changes.', new Error('Click refresh to check if they have finished their changes'));
            }
        } else {
            this.lockManager = null;
        }
        
        await this.loadSchoolList();

        this.busy = false;
    }
    
    public async loadSkill(): Promise<void> {
        if (!this.id) {
            this.skill = new Skill();
        } else {
            try {
                this.skill = await Skill.get(this.id);
            }
            catch (e) {
                this.error = true;
                this.$bus.$emit(BusEvent.ERROR, 'Could not get skill information', e);
            }
        }
    }
    
    public updateName() {
        let name = this.skill.name.getDefaultValue();
        if (!name) {
            name = '';
        }
        this.skillName = name;
    }
    
    public async loadSchoolList() {
        try {
            this.schoolList = await School.listAll();
            
            // hide school field if there is only 1 school and assign it as default
            if (this.schoolList.length === 1) {
                this.skill.school = this.schoolList.getIndex(0).id;
            } else {
                this.showSchools = true;
            }
        }
        catch (e) {
            this.$bus.$emit(BusEvent.ERROR, 'Could not load schools list', e);
            this.error = true;
        }
    }
    
    // enter edit mode if it can get the lock
    @Watch('skill', {deep: true})
    public async enterEditMode() {
        await this.tryToLock();
    }
    
    public async deleteClicked(): Promise<void> {
        if (!this.skill || !this.skill.id) {
            return;
        }
        
        this.busy = true;
        
        try {
            if (!this.lockManager){
                this.lockManager = new LockManager(Skill.API_SLUG, this.skill.id);
            }
            
            await this.tryToLock();
            
            await this.skill.delete();
            
            await this.lockManager.releaseLock();
            
            this.busy = false;
            this.$router.push(`/${Skill.SLUG}/`);
        }
        catch (e) {
            this.busy = false;
            this.$bus.$emit(BusEvent.ERROR, 'Could not delete skill', e);
        }
    }
    
    public async saveClicked(): Promise<void> {
        if (!this.skill) {
            return;
        }
        
        this.busy = true;
        
        try {
            this.checkMandatoryFields();
            
            await this.saveSkill();
            
            this.busy = false;
            this.$router.push(`/${Skill.SLUG}/${this.skill.id}`);
            
        } catch (e) {
            this.busy = false;
            this.$bus.$emit(BusEvent.ERROR, 'Could not save skill', e);
        }
    }
    
    public checkMandatoryFields() {
        // Check if name is filled
        const defaultName = this.skill.name.getDefaultValue();
        if(defaultName === undefined || defaultName === '') {
            throw Error('Please insert an English name for your skill.');
        }
        
        // Check if school field is filled
        if(this.skill.school === undefined) {
            throw Error('Please select a school.');
        }
        
        // Check if icon file was chosen
        if(!((this.$refs.icon as any) as MediaFileField).hasMedia()) {
            throw Error('Please select an icon.');
        }
    }
    
    public async saveSkill(): Promise<void> {
        const isNew = !this.skill.id;
        
        if (!isNew) {
            if (!this.lockManager && this.skill && this.skill.id){
                this.lockManager = new LockManager(Skill.API_SLUG, this.skill.id);
            }
            await this.tryToLock();
            
            // Upload media files (if exercise already created)
            await this.saveMediaFiles();
        }
        
        // for spreadsheets
        // Save simple files (that don't need a parent id)
        // await this.saveFiles();
        
        // Upload skill
        await this.skill.save();
        
        if (isNew) {
            await this.saveSkill();
        }
        
        /* for spreadsheets
        // Clear spreadsheet upload field
        this.skill.spreadsheet = null;
        if ('spreadsheet' in this.$refs && this.$refs.spreadsheet) {
            ((this.$refs.spreadsheet as any) as SimpleFileField).clear();
        }*/
        
        
        if (this.lockManager && this.lockManager.isLocked) {
            await this.lockManager.releaseLock();
        }
    }
    
    public async saveMediaFiles(): Promise<void> {
        if (!this.skill || !this.skill.id) {
            throw Error('Skill has not been created yet.');
        }
        
        this.imagesToDelete.forEach(async (id) => await Media.deleteImage(id));
        this.imagesToDelete = [];
        
        this.audioToDelete.forEach(async (id) => await Media.deleteAudio(id));
        this.audioToDelete = [];

        const icon = await this.saveMediaFile(this.$refs.icon, 'icon');
        if (icon !== null) {
            this.skill.icon = icon;
        }

        const icon_alt = await this.saveMediaFile(this.$refs.icon_alt, 'icon_alt');
        if (icon_alt !== null) {
            this.skill.icon_alt = icon_alt;
        }

        if (!this.isNew) {
            const bg_image = await this.saveMediaFile(this.$refs.bg_image, 'bg_image');
            if (bg_image !== null) {
                this.skill.bg_image = bg_image;
            }
        }
    }
    
    public async saveMediaFile(field: any, fieldName: string): Promise<number|null|undefined> {
        if (!this.skill || !this.skill.id){
            return null;
        }
        const mediaFileField = (field as any) as MediaFileField;
        if (mediaFileField.hasNewMedia()) {
            return await mediaFileField.upload(Skill.API_SLUG, this.skill.id, fieldName);
        } else if (!mediaFileField.hasMedia()) {
            return undefined;
        } else {
            return null;
        }
    }
    
    /* for spreadsheets
    public async saveFiles(): Promise<void> {
        if ('spreadsheet' in this.$refs && this.$refs.spreadsheet) {
            const spreadsheet = await this.saveFile(this.$refs.spreadsheet);
            if (spreadsheet !== null) {
                this.skill.spreadsheet = spreadsheet;
            }
        }
    }
    
    public async saveFile(field: any): Promise<string|null> {
        if (!this.skill) {
            throw Error('Missing skill data.')
        }
        
        const simpleFileField = (field as any) as SimpleFileField;
        if (simpleFileField.hasChanged()) {
            return await simpleFileField.upload();
        } else {
            return null;
        }
    }*/
}
